import {
  Button,
  Flex,
  Radio,
  RadioGroup,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';

import { ResolvedLoanFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { truncateUuid } from 'packages/common-frontend/utils';
import { LoanDetailsSideDrawer } from 'packages/invoices/ui/create/LoanDetailsSideDrawer';
import { Typography } from '~/packages/common-frontend/components/Typography';

export const CreateInvoiceLoanSelectorTableSkeleton = () => {
  return (
    <Table borderWidth="1px" borderColor={colors.gray[100]} mt="24px">
      <Thead>
        <Tr>
          <Th w="23%" fontSize="16px" backgroundColor={colors.background[100]}>
            Loan ID
          </Th>
          <Th w="23%" fontSize="16px" backgroundColor={colors.background[100]}>
            Loan Name
          </Th>
          <Th w="23%" fontSize="16px" backgroundColor={colors.background[100]}>
            Origination Date
          </Th>
          <Th w="23%" fontSize="16px" backgroundColor={colors.background[100]}>
            Maturity Date
          </Th>
          <Th w="8%" backgroundColor={colors.background[100]} />
        </Tr>
      </Thead>
      <Tbody>
        <Tr>
          <Td>
            <Skeleton height="24px" />
          </Td>
          <Td>
            <Skeleton height="24px" />
          </Td>
          <Td>
            <Skeleton height="24px" />
          </Td>
          <Td>
            <Skeleton height="24px" />
          </Td>
          <Td />
        </Tr>
      </Tbody>
    </Table>
  );
};

const LoanSelectorRow = ({ loan }: { loan: ResolvedLoanFragmentGQLType }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Tr key={loan.id}>
      <Td>
        <Radio value={loan.id}>
          <Flex alignItems="center" gap="1rem" ml="8px">
            <Typography>
              {truncateUuid(loan.id)}
              {loan.externalLoanId && ` (${loan.externalLoanId})`}
            </Typography>
          </Flex>
        </Radio>
      </Td>
      <Td>
        <Typography>{loan.name}</Typography>
      </Td>
      <Td>
        {commonDate.fullDateStrToHumanDateString(loan.dates.originationDate)}
      </Td>
      <Td>
        {commonDate.fullDateStrToHumanDateString(loan.dates.maturityDate)}
      </Td>
      <Td>
        <Button variant="v2-ghost" onClick={onOpen}>
          <Icon icon="chevron-right" />
        </Button>
      </Td>
      <LoanDetailsSideDrawer loan={loan} isOpen={isOpen} onClose={onClose} />
    </Tr>
  );
};

export const CreateInvoiceLoanSelectorTable = ({
  loans: allLoans,
}: {
  loans: ResolvedLoanFragmentGQLType[];
}) => {
  const {
    updateInvoice,
    invoice,
    selectedEntities: {
      loan: { updateLoan },
    },
  } = useCreateInvoiceContext();

  const filteredLoans = allLoans.filter((loan) => {
    // filter for loans with any positive outstanding balance, accrued interest, or accrued service fees
    // be sure to update this if we add more fees or liabilities that need to be allowed back in or filtered out
    return (
      (loan.funding.totalOutstandingBalance &&
        loan.funding.totalOutstandingBalance > 0) ||
      loan.fees.accruedInterest.totalAmount > 0 ||
      loan.fees.accruedServiceFees.totalAmount > 0
    );
  });

  return (
    <RadioGroup
      onChange={(selectedLoanId) => {
        const selectedLoan = filteredLoans.find(
          (loan) => loan.id === selectedLoanId,
        );

        finleyAssert(
          selectedLoan,
          `Selected loan must exist in Finley [finleyLoanId: ${selectedLoanId}]`,
        );

        updateInvoice({ field: 'finleyLoanId', value: selectedLoanId });
        updateLoan(selectedLoan);

        // auto-fill in other dates in the invoice with default values based on the selected loan
        updateInvoice({
          field: 'dueDate',
          value: selectedLoan.dates.maturityDate,
        });
        // @dani currently we do not want to auto-send invoice
        // updateInvoice({
        //   field: 'targetInvoiceDate',
        //   value: commonDate.addBusinessDays(
        //     selectedLoan.dates.maturityDate,
        //     -3,
        //   ),
        // });

        // set accrual start date to the next day after the most recent payment date, if any paid invoices exist on the loan
        updateInvoice({
          field: 'accrualStartDate',
          value: selectedLoan.invoices?.mostRecentPaymentDate
            ? commonDate.addDays(selectedLoan.invoices.mostRecentPaymentDate, 1)
            : selectedLoan.dates.originationDate,
        }); // @dani multi-invoice todo: deprecate. this will be moved down to the invoice component level
        updateInvoice({
          field: 'accrualEndDate',
          value: selectedLoan.dates.maturityDate,
        }); // @dani multi-invoice todo: deprecate. this will be moved down to the invoice component level
      }}
      value={invoice.finleyLoanId ?? undefined}
    >
      <Table borderWidth="1px" borderColor={colors.gray[100]} mt="24px">
        <Thead>
          <Tr>
            <Th
              w="23%"
              fontSize="16px"
              backgroundColor={colors.background[100]}
            >
              Loan ID
            </Th>
            <Th
              w="23%"
              fontSize="16px"
              backgroundColor={colors.background[100]}
            >
              Loan Name
            </Th>
            <Th
              w="23%"
              fontSize="16px"
              backgroundColor={colors.background[100]}
            >
              Origination Date
            </Th>
            <Th
              w="23%"
              fontSize="16px"
              backgroundColor={colors.background[100]}
            >
              Maturity Date
            </Th>
            <Th w="8%" backgroundColor={colors.background[100]} />
          </Tr>
        </Thead>
        <Tbody>
          {filteredLoans.map((loan) => {
            return <LoanSelectorRow key={loan.id} loan={loan} />;
          })}
        </Tbody>
      </Table>
    </RadioGroup>
  );
};
